import React, { useState } from "react";
import { IoWarningOutline } from "react-icons/io5";
import { ToggleSwitch } from "./ToggleSwitch";
// import { VisualOptions } from "./VisualOptions";
import { isFeatureEnabled } from "../config/featureFlags";
import { SongStructureEditor } from "./SongStructureEditor";
import { ProgressiveTempoEditor } from "./ProgressiveTempoEditor";
import { MdModeEdit } from "react-icons/md";
import { Tooltip } from "./Tooltip";
import { usePulseAnimation } from "../hooks/usePulseAnimation";

export type ToggleControlsProps = {
  dialFlashingEnabled: boolean;
  stressFirstBeat: boolean;
  screenFlashingEnabled: boolean;
  zenModeEnabled: boolean;
  beatNumbersEnabled: boolean;
  polyrhythmEnabled: boolean;
  onDialFlashingToggle: () => void;
  onStressFirstBeatToggle: () => void;
  onScreenFlashingToggle: () => void;
  onZenModeToggle: () => void;
  onBeatNumbersToggle: () => void;
  onPolyrhythmToggle: () => void;
  // Subdivisions controls
  subdivisionsEnabled?: boolean;
  onSubdivisionsToggle?: () => void;
  // Custom Song Structure controls
  customSongStructureOn?: boolean;
  onCustomSongStructureToggle?: () => void;
  onSongStructureSave?: (bars: import("./SongStructureEditor").Bar[]) => void;
  // Loop Structure controls
  loopStructure?: boolean;
  onLoopStructureToggle?: () => void;
  // Count-in controls
  countInEnabled?: boolean;
  onCountInToggle?: () => void;
  // Progressive Tempo controls
  progressiveTempoEnabled?: boolean;
  onProgressiveTempoToggle?: () => void;
  progressiveTempoResetEnabled?: boolean;
  onProgressiveTempoResetToggle?: () => void;
  // Timer controls
  timerEnabled?: boolean;
  onTimerToggle?: () => void;
};

export function ToggleControls({
  dialFlashingEnabled,
  stressFirstBeat,
  screenFlashingEnabled,
  zenModeEnabled,
  beatNumbersEnabled,
  polyrhythmEnabled,
  onDialFlashingToggle,
  onStressFirstBeatToggle,
  onScreenFlashingToggle,
  onZenModeToggle,
  onBeatNumbersToggle,
  onPolyrhythmToggle,
  subdivisionsEnabled,
  onSubdivisionsToggle,
  customSongStructureOn,
  onCustomSongStructureToggle,
  onSongStructureSave,
  loopStructure,
  onLoopStructureToggle,
  countInEnabled,
  onCountInToggle,
  progressiveTempoEnabled,
  onProgressiveTempoToggle,
  progressiveTempoResetEnabled,
  onProgressiveTempoResetToggle,
  timerEnabled,
  onTimerToggle,
}: ToggleControlsProps) {
  const WarningIcon = IoWarningOutline as React.ComponentType<any>;
  const PencilIcon = MdModeEdit as React.ComponentType<any>;
  const polyrhythmFeatureEnabled = isFeatureEnabled("polyrhythm");
  const customSongStructureEnabled = isFeatureEnabled("customSongStructure");
  const subdivisionsFeatureEnabled = isFeatureEnabled("subdivisions");
  const progressiveTempoFeatureEnabled = isFeatureEnabled("progressiveTempo");
  const timerFeatureEnabled = isFeatureEnabled("timer");

  const [internalCustomSongStructureOn, setInternalCustomSongStructureOn] =
    useState(() => {
      try {
        const stored = localStorage.getItem("customSongStructure.enabled");
        return stored === "true";
      } catch {
        return false;
      }
    });
  const [isSongStructureEditorOpen, setIsSongStructureEditorOpen] =
    useState(false);
  const [isProgressiveTempoEditorOpen, setIsProgressiveTempoEditorOpen] =
    useState(false);

  // Local state for Count-in toggle if not controlled via props
  const [internalCountInEnabled, setInternalCountInEnabled] = useState(false);

  // Pulse animation for the Edit button (match Mute button behavior)
  const {
    pulseState: editPulse,
    scaleState: editScale,
    triggerPulse: triggerEditPulse,
  } = usePulseAnimation();

  const openSongStructureEditor = () => {
    setIsSongStructureEditorOpen(true);
    try {
      window.dispatchEvent(
        new CustomEvent("customSongStructure:open", { detail: true })
      );
    } catch {}
  };

  const closeSongStructureEditor = () => {
    setIsSongStructureEditorOpen(false);
    try {
      window.dispatchEvent(
        new CustomEvent("customSongStructure:open", { detail: false })
      );
    } catch {}
  };

  const openProgressiveTempoEditor = () => {
    setIsProgressiveTempoEditorOpen(true);
    try {
      window.dispatchEvent(
        new CustomEvent("progressiveTempo:open", { detail: true })
      );
    } catch {}
  };

  const closeProgressiveTempoEditor = () => {
    setIsProgressiveTempoEditorOpen(false);
    try {
      window.dispatchEvent(
        new CustomEvent("progressiveTempo:open", { detail: false })
      );
    } catch {}
  };

  // Persist toggle to localStorage on change (works for controlled or internal state)
  React.useEffect(() => {
    const enabled = customSongStructureOn ?? internalCustomSongStructureOn;
    try {
      localStorage.setItem(
        "customSongStructure.enabled",
        enabled ? "true" : "false"
      );
    } catch {}
  }, [customSongStructureOn, internalCustomSongStructureOn]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "20px",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: "30px",
          justifyContent: "center",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <ToggleSwitch
          enabled={stressFirstBeat}
          onToggle={onStressFirstBeatToggle}
          label="Stress Beat"
          tooltip="Press any beat dot to make it stressed"
          tooltipShortcut="B"
        />

        <ToggleSwitch
          enabled={dialFlashingEnabled}
          onToggle={onDialFlashingToggle}
          label="Flash Dial"
          tooltip="Flash the BPM dial on each beat"
          tooltipShortcut="D"
        />

        {polyrhythmFeatureEnabled && (
          <ToggleSwitch
            enabled={polyrhythmEnabled}
            onToggle={onPolyrhythmToggle}
            label="Polyrhythm Mode"
          />
        )}

        <ToggleSwitch
          enabled={screenFlashingEnabled}
          onToggle={onScreenFlashingToggle}
          label={
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <WarningIcon
                style={{
                  fontSize: "20px",
                }}
              />
              Flash Screen
            </div>
          }
          tooltip="Warning: Flashing lights may trigger seizures for people with photosensitive epilepsy"
          tooltipShortcut="F"
        />

        <ToggleSwitch
          enabled={zenModeEnabled}
          onToggle={onZenModeToggle}
          label="Zen Mode"
          tooltip="Hide all controls and focus on the beats - Click anywhere to return"
          tooltipShortcut="Z"
        />

        <ToggleSwitch
          enabled={beatNumbersEnabled}
          onToggle={onBeatNumbersToggle}
          label="Beat Numbers"
          tooltip="Show numbers above each beat dot"
        />

        {timerFeatureEnabled && (
          <ToggleSwitch
            enabled={timerEnabled ?? false}
            onToggle={onTimerToggle ?? (() => {})}
            label="Timer"
            tooltip="Set a timer and the metronome will play until the time runs out"
            tooltipShortcut="T"
          />
        )}
      </div>

      {/* Subdivisions and Custom Song Structure Row */}
      {(subdivisionsFeatureEnabled ||
        customSongStructureEnabled ||
        progressiveTempoFeatureEnabled) && (
        <div
          key={`custom-song-row-${
            customSongStructureOn ?? internalCustomSongStructureOn
          }-${subdivisionsEnabled}-${progressiveTempoEnabled}`}
          className="custom-song-structure-row"
          style={{
            display: "flex",
            gap: "30px",
            justifyContent: "center",
            alignItems: "center",
            marginTop: "8px",
            flexWrap: "wrap",
          }}
        >
          {/* Custom Song Structure Toggle - fades out when Subdivisions or Progressive Tempo is on */}
          {customSongStructureEnabled && (
            <div
              className={`custom-song-structure-toggle-container ${
                subdivisionsEnabled || progressiveTempoEnabled ? "fade-out" : ""
              }`}
            >
              <ToggleSwitch
                enabled={
                  (customSongStructureOn ??
                    internalCustomSongStructureOn) as boolean
                }
                onToggle={() => {
                  if (onCustomSongStructureToggle) {
                    onCustomSongStructureToggle();
                  } else {
                    setInternalCustomSongStructureOn((prev) => !prev);
                  }
                }}
                label="Custom Song Structure"
                tooltip="Define per-bar BPM and time signature"
                tooltipShortcut="C"
                disabled={subdivisionsEnabled || progressiveTempoEnabled}
              />
            </div>
          )}

          {/* Subdivisions Toggle - fades out when Custom Song Structure is on */}
          {subdivisionsFeatureEnabled && (
            <div
              className={`subdivisions-toggle-container ${
                customSongStructureOn ?? internalCustomSongStructureOn
                  ? "fade-out"
                  : ""
              }`}
            >
              <ToggleSwitch
                enabled={subdivisionsEnabled ?? false}
                onToggle={onSubdivisionsToggle ?? (() => {})}
                label="Subdivisions"
                tooltipShortcut="S"
                tooltip="Show Subdivisions"
              />
            </div>
          )}

          {/* Progressive Tempo Toggle - fades out when Custom Song Structure is on */}
          {progressiveTempoFeatureEnabled && (
            <div
              className={`progressive-tempo-toggle-container ${
                customSongStructureOn ?? internalCustomSongStructureOn
                  ? "fade-out"
                  : ""
              }`}
            >
              <ToggleSwitch
                enabled={progressiveTempoEnabled ?? false}
                onToggle={onProgressiveTempoToggle ?? (() => {})}
                label="Progressive Tempo"
                tooltipShortcut="P"
                tooltip="Gradually increase the tempo over time"
                disabled={
                  customSongStructureOn ??
                  internalCustomSongStructureOn ??
                  false
                }
              />
            </div>
          )}

          {/* Edit button for Progressive Tempo - appears when Progressive Tempo is on */}
          {progressiveTempoFeatureEnabled && (
            <div
              className={`edit-button-container ${
                progressiveTempoEnabled ? "visible" : ""
              }`}
            >
              <Tooltip text="Edit progressive tempo settings" position="top">
                <button
                  aria-label="Edit Progressive Tempo"
                  onClick={(e) => {
                    triggerEditPulse(e);
                    openProgressiveTempoEditor();
                  }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "40px",
                    height: "40px",
                    borderRadius: "12px",
                    border: "none",
                    background: "var(--toggle-bg)",
                    color: "var(--text-primary)",
                    cursor: "pointer",
                    fontWeight: 500,
                    backdropFilter: "blur(7px)",
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                    transition: "all 0.3s ease",
                    position: "relative",
                    overflow: "hidden",
                    transform: `translateY(0) scale(${editScale.scale})`,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow =
                      "0 12px 40px rgba(0, 0, 0, 0.2)";
                    e.currentTarget.style.background = "var(--button-hover-bg)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = `translateY(0) scale(${editScale.scale})`;
                    e.currentTarget.style.boxShadow =
                      "0 8px 32px rgba(0, 0, 0, 0.1)";
                    e.currentTarget.style.background = "var(--toggle-bg)";
                  }}
                >
                  {/* Pulse Animation Overlay */}
                  {editPulse.isActive && (
                    <div
                      style={{
                        position: "absolute",
                        left: editPulse.x,
                        top: editPulse.y,
                        width: "0",
                        height: "0",
                        borderRadius: "50%",
                        background: "var(--button-hover-bg)",
                        transform: "translate(-50%, -50%)",
                        animation: "pulseExpand 100ms ease-out forwards",
                        pointerEvents: "none",
                        zIndex: 1,
                      }}
                    />
                  )}
                  <PencilIcon style={{ fontSize: "18px" }} />
                </button>
              </Tooltip>
            </div>
          )}

          {/* Reset BPM when stopped toggle - appears when Progressive Tempo is on */}
          {progressiveTempoFeatureEnabled && (
            <div
              className={`progressive-tempo-reset-toggle-container ${
                progressiveTempoEnabled ? "visible" : ""
              }`}
            >
              <ToggleSwitch
                enabled={progressiveTempoResetEnabled ?? false}
                onToggle={onProgressiveTempoResetToggle ?? (() => {})}
                label="Reset BPM when stopped"
                tooltip="Reset BPM to starting value when metronome stops"
              />
            </div>
          )}

          {/* Edit button - appears when Custom Song Structure is on */}
          {customSongStructureEnabled && (
            <div
              className={`edit-button-container ${
                customSongStructureOn ?? internalCustomSongStructureOn
                  ? "visible"
                  : ""
              }`}
            >
              <Tooltip text="Edit song structure" position="top">
                <button
                  aria-label="Edit Song Structure"
                  onClick={(e) => {
                    triggerEditPulse(e);
                    openSongStructureEditor();
                  }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "40px",
                    height: "40px",
                    borderRadius: "12px",
                    border: "none",
                    background: "var(--toggle-bg)",
                    color: "var(--text-primary)",
                    cursor: "pointer",
                    fontWeight: 500,
                    backdropFilter: "blur(7px)",
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                    transition: "all 0.3s ease",
                    position: "relative",
                    overflow: "hidden",
                    transform: `translateY(0) scale(${editScale.scale})`,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow =
                      "0 12px 40px rgba(0, 0, 0, 0.2)";
                    e.currentTarget.style.background = "var(--button-hover-bg)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = `translateY(0) scale(${editScale.scale})`;
                    e.currentTarget.style.boxShadow =
                      "0 8px 32px rgba(0, 0, 0, 0.1)";
                    e.currentTarget.style.background = "var(--toggle-bg)";
                  }}
                >
                  {/* Pulse Animation Overlay */}
                  {editPulse.isActive && (
                    <div
                      style={{
                        position: "absolute",
                        left: editPulse.x,
                        top: editPulse.y,
                        width: "0",
                        height: "0",
                        borderRadius: "50%",
                        background: "var(--button-hover-bg)",
                        transform: "translate(-50%, -50%)",
                        animation: "pulseExpand 100ms ease-out forwards",
                        pointerEvents: "none",
                        zIndex: 1,
                      }}
                    />
                  )}
                  <PencilIcon style={{ fontSize: "18px" }} />
                </button>
              </Tooltip>
            </div>
          )}

          {/* Loop toggle - appears when Custom Song Structure is on */}
          {customSongStructureEnabled && (
            <div
              className={`loop-toggle-container ${
                customSongStructureOn ?? internalCustomSongStructureOn
                  ? "visible"
                  : ""
              }`}
            >
              <ToggleSwitch
                enabled={loopStructure ?? false}
                onToggle={onLoopStructureToggle ?? (() => {})}
                label="Loop"
                tooltip="Loop the current song structure"
                tooltipShortcut="L"
              />
            </div>
          )}

          {/* Count-in toggle - appears when Custom Song Structure is on */}
          {customSongStructureEnabled && (
            <div
              className={`countin-toggle-container ${
                customSongStructureOn ?? internalCustomSongStructureOn
                  ? "visible"
                  : ""
              }`}
            >
              <ToggleSwitch
                enabled={countInEnabled ?? internalCountInEnabled}
                onToggle={() => {
                  if (onCountInToggle) {
                    onCountInToggle();
                  } else {
                    setInternalCountInEnabled((prev) => !prev);
                  }
                }}
                label="Count-in"
                tooltip="Play the current bar once before the song starts"
              />
            </div>
          )}
        </div>
      )}

      <SongStructureEditor
        isOpen={isSongStructureEditorOpen}
        onCancel={closeSongStructureEditor}
        onSave={(bars) => {
          onSongStructureSave?.(bars);
          closeSongStructureEditor();
        }}
        loop={loopStructure}
        onLoopChange={onLoopStructureToggle}
      />

      <ProgressiveTempoEditor
        isOpen={isProgressiveTempoEditorOpen}
        onCancel={closeProgressiveTempoEditor}
        onSave={(increment, frequency) => {
          closeProgressiveTempoEditor();
        }}
      />
    </div>
  );
}
